import {
    ActivityCategory,
    activityCategoryLabels,
    activityCategoryLogLabels,
    ActivityRecordType,
    ActivityTargetView,
    categoryActivityTarget,
    genericLogLabel,
    ReferenceContactId,
    serializeActivityTarget,
    taskTypeActivityTarget,
    TaskTypeId,
} from "@sp-crm/core";
import { EntityDetails } from "components/details-page-v-next/entity-contact-details";
import { RecentActivityFeedItem } from "components/details-page-v-next/recent-activities-feed";
import { labeledContactPhones } from "components/references/show-reference/reference-details";
import {
    ActivityTypeOption,
    activityTypesForRecordType,
} from "components/tasks/activity-type-options";
import { TouchpointStatusView } from "./marketing-plan-format";

export const marketingPlanRecordType: ActivityRecordType = "referenceContact";

export const activityTypesForMarketingPlan = (
    activityTypes: ActivityTypeOption[],
    targets: ActivityTargetView[],
): ActivityTypeOption[] =>
    activityTypesForRecordType(
        activityTypes,
        marketingPlanRecordType,
        targets.flatMap(target => (target.taskTypeId ? [target.taskTypeId] : [])),
    );

export interface AgendaFeedTouchpoint {
    touchpointId: string;
    targets: ActivityTargetView[];
    intervalCount: number;
    intervalUnit: string;
    state?: TouchpointStatusView["state"];
    lastContactedAt?: string | null;
    nextDueAt?: string | null;
    pendingVisit?: TouchpointStatusView["pendingVisit"];
}

export interface AgendaFeedContact {
    contactId: ReferenceContactId;
    contactName: string;
    orgName?: string | null;
    contactEmail?: string | null;
    contactPhone?: string | null;
    contactCellPhone?: string | null;
    contactAltPhone?: string | null;
    contactAddress?: string | null;
    contactEmailOptedOut: boolean;
    tierListItemId: string;
    tierName: string;
    tierColor?: string | null;
    touchpoints: AgendaFeedTouchpoint[];
    satisfiedTouchpoints?: AgendaFeedTouchpoint[];
    recentActivity: AgendaRecentActivityItem[];
}

export interface AgendaFeed {
    contacts: AgendaFeedContact[];
    completedContactIds: string[];
}

export interface AgendaTouchpoint extends TouchpointStatusView {
    touchpointId: string;
    targets: ActivityTargetView[];
    intervalCount: number;
    intervalUnit: string;
}

export const targetCategories = (targets: ActivityTargetView[]): Set<ActivityCategory> =>
    new Set(targets.map(target => target.category));

export interface LoggedActivityChoice {
    key: string;
    label: string;
    category: ActivityCategory;
    taskTypeId: TaskTypeId | null;
}

export interface LoggedActivityGroup {
    category: ActivityCategory;
    label: string;
    choices: LoggedActivityChoice[];
}

const typeChoice = (option: ActivityTypeOption): LoggedActivityChoice => ({
    key: serializeActivityTarget(taskTypeActivityTarget(option.id)),
    label: option.name,
    category: option.category,
    taskTypeId: option.id,
});

const wholeCategoryChoice = (category: ActivityCategory): LoggedActivityChoice => ({
    key: serializeActivityTarget(categoryActivityTarget(category)),
    label: activityCategoryLabels[category],
    category,
    taskTypeId: null,
});

export const activityChoicesForCategory = (
    targets: ActivityTargetView[],
    category: ActivityCategory,
    activityTypes: ActivityTypeOption[],
): LoggedActivityChoice[] => {
    const inCategory = targets.filter(target => target.category === category);
    const scopedTypeIds = inCategory.flatMap(target =>
        target.kind === "taskType" ? [target.taskTypeId] : [],
    );
    if (!inCategory.some(target => target.kind === "category")) {
        return activityTypes
            .filter(option => scopedTypeIds.includes(option.id))
            .map(typeChoice);
    }
    const offerable = activityTypes.filter(
        option =>
            option.category === category &&
            (!option.isArchived || scopedTypeIds.includes(option.id)),
    );
    return offerable.length > 0
        ? offerable.map(typeChoice)
        : [wholeCategoryChoice(category)];
};

export const loggedActivityGroups = (
    targets: ActivityTargetView[],
    activityTypes: ActivityTypeOption[],
): LoggedActivityGroup[] => {
    const categories: ActivityCategory[] = [];
    for (const target of targets) {
        if (!categories.includes(target.category)) {
            categories.push(target.category);
        }
    }
    return categories.map(category => ({
        category,
        label: activityCategoryLabels[category],
        choices: activityChoicesForCategory(targets, category, activityTypes),
    }));
};

export const loggedActivityChoices = (
    groups: LoggedActivityGroup[],
): LoggedActivityChoice[] => groups.flatMap(group => group.choices);

export interface AgendaContactGroup {
    contactId: ReferenceContactId;
    contactName: string;
    orgName: string | null;
    contactEmail: string | null;
    contactPhone: string | null;
    contactCellPhone: string | null;
    contactAltPhone: string | null;
    contactAddress: string | null;
    contactEmailOptedOut: boolean;
    tierListItemId: string;
    tierName: string | null;
    tierColor: string | null;
    touchpoints: AgendaTouchpoint[];
    recentActivity: AgendaRecentActivityItem[];
}

const toAgendaTouchpoint = (touchpoint: AgendaFeedTouchpoint): AgendaTouchpoint => ({
    touchpointId: touchpoint.touchpointId,
    targets: touchpoint.targets,
    intervalCount: touchpoint.intervalCount,
    intervalUnit: touchpoint.intervalUnit,
    lastContactedAt: touchpoint.lastContactedAt ?? null,
    nextDueAt: touchpoint.nextDueAt ?? null,
    state: touchpoint.state ?? "due",
    pendingVisit: touchpoint.pendingVisit ?? null,
});

export const agendaContactGroups = (
    contacts: AgendaFeedContact[],
): AgendaContactGroup[] =>
    contacts.map(contact => ({
        contactId: contact.contactId,
        contactName: contact.contactName,
        orgName: contact.orgName ?? null,
        contactEmail: contact.contactEmail ?? null,
        contactPhone: contact.contactPhone ?? null,
        contactCellPhone: contact.contactCellPhone ?? null,
        contactAltPhone: contact.contactAltPhone ?? null,
        contactAddress: contact.contactAddress ?? null,
        contactEmailOptedOut: contact.contactEmailOptedOut,
        tierListItemId: contact.tierListItemId,
        tierName: contact.tierName,
        tierColor: contact.tierColor ?? null,
        touchpoints: [
            ...contact.touchpoints,
            ...(contact.satisfiedTouchpoints ?? []),
        ].map(toAgendaTouchpoint),
        recentActivity: contact.recentActivity,
    }));

export const agendaContactDetails = (group: AgendaContactGroup): EntityDetails => ({
    classification: null,
    classificationLabel: "Role",
    phones: labeledContactPhones({
        cell: group.contactCellPhone,
        alternate: group.contactAltPhone,
    }),
    email: group.contactEmail,
    emailOptOut: group.contactEmailOptedOut,
    address: group.contactAddress,
    website: null,
    organization: null,
});

export interface AgendaCard {
    group: AgendaContactGroup;
    isCelebrating: boolean;
}

export interface AgendaMergeInput {
    groups: AgendaContactGroup[];
    snoozedContactIds: string[];
    completedContactIds: string[];
}

export const mergeAgendaCards = (
    previous: AgendaCard[],
    incoming: AgendaMergeInput,
): AgendaCard[] => {
    const byContactId = new Map(incoming.groups.map(group => [group.contactId, group]));
    const snoozed = new Set(incoming.snoozedContactIds);
    const completed = new Set<string>(incoming.completedContactIds);
    const kept = previous.flatMap(card => {
        const group = byContactId.get(card.group.contactId);
        if (group) {
            return [{ group, isCelebrating: false }];
        }
        const contactId = card.group.contactId;
        return !snoozed.has(contactId) && completed.has(contactId)
            ? [{ group: card.group, isCelebrating: true }]
            : [];
    });
    const shown = new Set(kept.map(card => card.group.contactId));
    const added = incoming.groups
        .filter(group => !shown.has(group.contactId))
        .map(group => ({ group, isCelebrating: false }));
    return [...kept, ...added];
};

export type AgendaRecentActivityItem = RecentActivityFeedItem;

export const filterCardsByTier = (
    cards: AgendaCard[],
    selectedTierIds: string[],
): AgendaCard[] => {
    if (selectedTierIds.length === 0) {
        return cards;
    }
    const selected = new Set(selectedTierIds);
    return cards.filter(card => selected.has(card.group.tierListItemId));
};

export const logLabelForCategory = (category: ActivityCategory | null): string =>
    category ? activityCategoryLogLabels[category] : genericLogLabel;

export type EmailActionState = "send-email" | "add-email" | "email-opted-out";

export const resolveEmailAction = (input: {
    contactEmail: string | null;
    contactEmailOptedOut: boolean;
}): EmailActionState => {
    if (input.contactEmailOptedOut) {
        return "email-opted-out";
    }
    if (input.contactEmail) {
        return "send-email";
    }
    return "add-email";
};

export interface TouchpointActions {
    email: EmailActionState | null;
    addContactPhone: boolean;
    provisionPhoneLine: boolean;
    call: boolean;
    text: boolean;
    visit: boolean;
}

export const resolveTouchpointActions = (input: {
    targets: ActivityTargetView[];
    contactEmail: string | null;
    contactEmailOptedOut: boolean;
    hasContactPhone: boolean;
    hasPhoneLine: boolean;
}): TouchpointActions => {
    const categories = targetCategories(input.targets);
    const wantsCall = categories.has("call");
    const wantsText = categories.has("sms");
    const wantsPhone = wantsCall || wantsText;
    const callable = wantsPhone && input.hasContactPhone && input.hasPhoneLine;
    return {
        email: categories.has("email") ? resolveEmailAction(input) : null,
        addContactPhone: wantsPhone && !input.hasContactPhone,
        provisionPhoneLine: wantsPhone && input.hasContactPhone && !input.hasPhoneLine,
        call: wantsCall && callable,
        text: wantsText && callable,
        visit: categories.has("visit"),
    };
};

export const agendaNeedsPhoneProvisioning = (
    groups: AgendaContactGroup[],
    hasPhoneLine: boolean,
): boolean => {
    if (hasPhoneLine) {
        return false;
    }
    return groups.some(group =>
        group.touchpoints.some(touchpoint => {
            if (touchpoint.state !== "due") {
                return false;
            }
            const categories = targetCategories(touchpoint.targets);
            return categories.has("call") || categories.has("sms");
        }),
    );
};
